/*
 * Module for the config area of the Learner
 */

define([
  "i18n/i18n",
  "marionette",
  "moment",
  "messaging/messenger",
  "configuration/properties",
  "modules/accountAdmin/kbAdmin/kbModel",
  "modules/learner/learnerConfig/learnerConfigView",
  "modules/learner/learnerModel",
], function (
  i18next,
  Marionette,
  Moment,
  Messenger,
  Properties,
  KbModel,
  LearnerConfigView,
  LearnerModel,
) {
  "use strict";

  const DEFAULT_MATCH_TYPE = 3;

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "learnerConfig";
      this.region = options.region;
      //the touchpoint ID (maybe undefined) to use instead of the default
      this.touchpointId = options.touchpointId;
      this.localeId = options.localeId;
      this._setUpVents();
    },
    show: async function (options = {}) {
      const model = Messenger.reqres.request("learner:search:model");
      this.model = model;
      const currentKb = Messenger.reqres.request("active:kb:model");
      const { clusteringJobDetails, shouldTriggerSearch, shouldDisableSearch } = options;
      this.shouldDisableSearch = shouldDisableSearch;
      // clusteringJobDetails should have a value only for semantic kb
      this.clusteringJobDetails = clusteringJobDetails;
      this.shouldTriggerSearch = shouldTriggerSearch;
      model.isSemanticKb = currentKb.get("engineMode") === KbModel.ENGINE_MODE.SEMANTIC;
      if (model.isSemanticKb && clusteringJobDetails && clusteringJobDetails.get("jobId")) {
        this._setClusterJobParametersToModel(clusteringJobDetails);
      } else {
        this._setDefaultLearnerConfigParametersToModel();
      }

      this.view = new LearnerConfigView.learnerConfigView({ model: model });

      this.view.on("click:search", this.searchLearnerData, this);

      this.view.on("reset:clustering:job", this.resetClusteringJob, this);

      this.view.on("show", this._triggerSearchAndDisableSearchIfNeeded, this);

      await model.get("touchpoints").onSyncedPromise();
      await model.get("authoringStatuses").onSyncedPromise();
      await model.get("locales").onSyncedPromise();

      this.showRegion(this.view);
    },
    disableLearnerSearch: function () {
      this.view.disableLearnerSearch();
    },
    enableLearnerSearch: function () {
      this.view.enableLearnerSearch();
    },
    resetClusteringJob: function () {
      if (
        !this.model.isSemanticKb ||
        !this.clusteringJobDetails ||
        this.clusteringJobDetails.isClusteringJobPending()
      ) {
        // Do not reset the clustering job if it is still pending. User should be forced to wait for the job to finish.
        return;
      }
      this.clusteringJobDetails = null;
      this.enableLearnerSearch();
    },
    searchLearnerData: async function () {
      const data = this.view.getLearnerConfig();
      if (
        this.model.isSemanticKb &&
        this.clusteringJobDetails &&
        this.clusteringJobDetails.get("jobId")
      ) {
        // Fetch clusters using job ID
        data.jobId = this.clusteringJobDetails.get("jobId");
        await this._fetchClustersAndShowResults(data);
      } else if (data.cluster) {
        // if clustering is enabled, set the threshold
        // get transcripts count
        try {
          if (this.model.isSemanticKb) {
            const countModel = Messenger.reqres.request("learner:transcripts:count", data);
            await countModel.onSyncedPromise();
            if (countModel.get("value") > Properties.clusteringJobQueryCountThreshold) {
              // submit a clustering job and start polling for status
              const jobModel = Messenger.reqres.request("learner:submit:clustering:job", data);
              await jobModel.onSyncedPromise();
              // hide the search results region and show the job submitted message
              Messenger.vent.trigger("clustering:job:submitted", jobModel);
            } else {
              await this._fetchClustersAndShowResults(data);
            }
          } else {
            await this._fetchClustersAndShowResults(data);
          }
        } catch (errorMessage) {
          console.error("Error fetching transcripts count:", errorMessage);
          Messenger.okDialog(i18next.t("learner.results.searchError"));
        }
      } else {
        this._fetchUnclusteredTranscriptsAndShowResults(data);
      }
    },
    _setDefaultLearnerConfigParametersToModel: function () {
      const model = this.model;
      // Figure out how many days back to go
      const daysBack = new Date().getDefaultDaysBack();
      model.set("startDaysBack", daysBack);
      // override the default touchpoint if this.touchpointId is defined
      if (this.touchpointId) {
        if (_.isString(this.touchpointId) && this.touchpointId.indexOf("_") !== -1) {
          const touchpoints = this.touchpointId.split("_");
          const array = [];
          for (let i = 0; i < touchpoints.length; i++) {
            array.push(touchpoints[i]);
          }
          model.set("selectedTouchpointId", array);
        } else {
          //put it in an array so the same code works for one as multiple
          model.set("selectedTouchpointId", [this.touchpointId]);
        }
      } else if (model.get("defaultTouchpoint")) {
        model.set("selectedTouchpointId", [model.get("defaultTouchpoint")]);
      } else {
        //set an empty array as selected so the HTML won't blow up
        model.set("selectedTouchpointId", []);
      }
      if (this.localeId) {
        model.set("selectedLocaleId", this.localeId);
      } else {
        model.set("selectedLocaleId", Messenger.reqres.request("default:response:language"));
      }
      // set all empty values for semantic kb clustering job related data so that we do not break the html
      model.set("selectedMatchType", DEFAULT_MATCH_TYPE);
      model.set("selectedAuthoringStatus", "");
      model.set("selectedClusterThresholdSliderValue", "");
      model.set("selectedStartDaysBack", "");
      model.set("selectedEndDate", "");
      model.set("selectedStartTimeValue", "");
      model.set("selectedEndTimeValue", model.get("endDateMilliOffset"));
    },
    _setClusterJobParametersToModel: function (clusteringJobDetails) {
      const model = this.model;
      model.set("selectedTouchpointId", clusteringJobDetails.get("touchpointIds"));
      model.set("selectedLocaleId", clusteringJobDetails.get("localeIds")[0]);
      model.set("selectedMatchType", +clusteringJobDetails.get("matchingStatus"));
      model.set("selectedAuthoringStatus", clusteringJobDetails.get("authoringStatus"));
      model.set(
        "selectedClusterThresholdSliderValue",
        model.getSemanticClusterSliderValueFromThreshold(
          clusteringJobDetails.get("clusterThreshold"),
        ),
      );
      const startDateInMs = clusteringJobDetails.get("startDate");
      const endDateInMs = clusteringJobDetails.get("endDate");
      model.set("selectedStartDaysBack", this._findDaysBack(startDateInMs));
      model.set("selectedEndDate", endDateInMs);
      model.set("selectedStartTimeValue", this._findTimeOfTheDay(startDateInMs));
      model.set("selectedEndTimeValue", this._findTimeOfTheDay(endDateInMs));
    },
    _fetchUnclusteredTranscriptsAndShowResults: function (data) {
      //get unclustered search results
      const learnerTranscripts = Messenger.reqres.request("learner:search:results", data);
      Messenger.vent.trigger(
        "show:learner:results",
        learnerTranscripts,
        false,
        parseInt(data.matchType, 10),
        data.localeId,
      );
    },
    _fetchClustersAndShowResults: async function (data) {
      //get clustered search results
      const clusteredTranscripts = Messenger.reqres.request(
        "learner:clustered:search:results",
        data,
      );
      Messenger.vent.trigger("show:saving:display");
      try {
        await clusteredTranscripts.onSyncedPromise();
        Messenger.vent.trigger(
          "show:learner:results",
          clusteredTranscripts,
          true,
          parseInt(data.matchType, 10),
          data.localeId,
        ); //true means transcripts are clustered
      } catch (errorMsg) {
        console.error("Could not load clustered transcripts", errorMsg);
        Messenger.okDialog(errorMsg);
      } finally {
        Messenger.vent.trigger("close:saving:display");
      }
    },
    _findTimeOfTheDay: function (timestampInMs) {
      if (!timestampInMs) {
        return null;
      }
      const m = Moment(timestampInMs);
      // Difference between the timestamp and the start of its own day
      return m.diff(m.clone().startOf("day"));
    },
    _findDaysBack: function (timestampInMs) {
      if (!timestampInMs) {
        return null;
      }
      // Normalize the current date and given timestamp to the start of the day (00:00:00)
      const today = Moment().startOf("day");
      const targetDay = Moment(timestampInMs).startOf("day");

      // Calculate the difference in calendar days
      return today.diff(targetDay, "days");
    },
    //trigger search externally using this function if we want to cause a search to happen without hitting the search button
    triggerSearch: function () {
      if (this.view) {
        this.view.trigger("click:search");
      }
    },
    _triggerSearchAndDisableSearchIfNeeded: function () {
      if (this.view) {
        if (this.shouldTriggerSearch) {
          this.view.trigger("click:search");
        }
        if (this.shouldDisableSearch) {
          this.view.disableLearnerSearch();
        }
      }
    },
    //Intended to be used by other areas in the learner that need config settings, namely the download option
    _setUpVents: function () {
      Messenger.reqres.setHandler(
        "learner:config:settings",
        function () {
          return this.view.getLearnerConfig();
        },
        this,
      );
    },
  });
});
